import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Trash, Warning, CaretLeft, CaretRight, Prohibit } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_SLOTS = [];
for (let h = 8; h <= 21; h++) {
  for (let m = 0; m < 60; m += 15) {
    if (h === 21 && m > 0) break;
    TIME_SLOTS.push({ h, m, label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
  }
}

const WEEKDAYS_ES = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfWeek(d) {
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const r = new Date(d);
  r.setDate(r.getDate() - diff);
  return r;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function slotIndex(timeStr) {
  return TIME_SLOTS.findIndex(s => s.label === timeStr);
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Panel para gestionar bloqueos de hueco de un estudio con un calendario
 * visual (arrastrar para seleccionar franjas, igual que la programacion de
 * visitas).  El bloqueo solo afecta al estudio indicado.
 */
export default function ScheduleBlocksPanel({ studyId, studyColor = '#1f3e80', lang = 'es' }) {
  const [blocks, setBlocks] = useState([]);
  const [slots, setSlots] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [dragDay, setDragDay] = useState(null);
  const [pendingBlock, setPendingBlock] = useState(null); // { date, start_time, end_time }
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const tableRef = useRef(null);

  const lb = lang === 'es'
    ? {
        title: 'Bloqueos de hueco',
        subtitle: 'Selecciona franjas en el calendario arrastrando con el raton para bloquearlas en este estudio. No afectan al resto de estudios.',
        today: 'Hoy',
        drag_hint: 'Pulsa y arrastra en el calendario para seleccionar las franjas a bloquear',
        schedule_legend: 'Programacion del estudio',
        block_legend: 'Bloqueos',
        empty: 'Este estudio no tiene bloqueos configurados.',
        new_block_title: 'Nuevo bloqueo',
        new_block_desc: 'Indica el motivo del bloqueo. Este texto aparecera en el calendario y en la auditoria.',
        range: 'Rango',
        reason: 'Motivo',
        reason_ph: 'Ej: Reunion con sponsor, calibracion equipos, mantenimiento sala...',
        save: 'Crear bloqueo',
        cancel: 'Cancelar',
        close: 'Cerrar',
        date_col: 'Fecha',
        start: 'Inicio',
        end: 'Fin',
        created_by: 'Creado por',
        conflicts_title: 'Atencion: citas afectadas',
        conflicts_subtitle: 'El bloqueo se ha creado correctamente, pero las siguientes citas ya estan programadas en ese rango. No se han eliminado; reprogramalas o cancelalas desde la Agenda:',
        del_title: 'Eliminar bloqueo',
        del_desc: '¿Estas seguro de que deseas eliminar este bloqueo? Los huecos volveran a estar disponibles para citas.',
        saved: 'Bloqueo creado',
        deleted: 'Bloqueo eliminado',
        list_title: 'Bloqueos activos',
      }
    : {
        title: 'Schedule blocks',
        subtitle: 'Drag on the calendar to block time slots specifically for this study. Does not affect other studies.',
        today: 'Today',
        drag_hint: 'Click and drag on the calendar to select slots to block',
        schedule_legend: 'Study scheduling',
        block_legend: 'Blocks',
        empty: 'This study has no blocks configured.',
        new_block_title: 'New block',
        new_block_desc: 'Provide a reason for the block. This text appears in the calendar and in the audit trail.',
        range: 'Range',
        reason: 'Reason',
        reason_ph: 'E.g. Sponsor meeting, probe calibration, maintenance...',
        save: 'Create block',
        cancel: 'Cancel',
        close: 'Close',
        date_col: 'Date',
        start: 'Start',
        end: 'End',
        created_by: 'Created by',
        conflicts_title: 'Warning: conflicting appointments',
        conflicts_subtitle: 'The block was created successfully, but the following appointments already exist in that range. They were NOT deleted; please reschedule or cancel them from the Agenda:',
        del_title: 'Delete block',
        del_desc: 'Are you sure? Slots will become available for appointments again.',
        saved: 'Block created',
        deleted: 'Block deleted',
        list_title: 'Active blocks',
      };

  const WEEKDAYS = lang === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;
  const MONTHS = lang === 'es' ? MONTHS_ES : MONTHS_EN;

  const loadData = useCallback(async () => {
    if (!studyId) return;
    try {
      const [bks, sch] = await Promise.all([
        axios.get(`${API_URL}/api/schedule/blocks?study_id=${studyId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/schedule`, { withCredentials: true }),
      ]);
      setBlocks(Array.isArray(bks.data) ? bks.data : []);
      setSlots(Array.isArray(sch.data) ? sch.data : []);
    } catch {
      setBlocks([]);
      setSlots([]);
    }
  }, [studyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = fmtDate(new Date());

  const navigate = (dir) => setCurrentDate(prev => addDays(prev, dir * 7));

  const handleMouseDown = (dayStr, slotLabel) => {
    setIsDragging(true);
    setDragDay(dayStr);
    setDragStart(slotLabel);
    setDragEnd(slotLabel);
  };

  const handleMouseMove = (dayStr, slotLabel) => {
    if (!isDragging || dayStr !== dragDay) return;
    setDragEnd(slotLabel);
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd || !dragDay) { setIsDragging(false); return; }
    setIsDragging(false);
    const startIdx = slotIndex(dragStart);
    const endIdx = slotIndex(dragEnd);
    const fromIdx = Math.min(startIdx, endIdx);
    const toIdx = Math.max(startIdx, endIdx);
    const startTime = TIME_SLOTS[fromIdx].label;
    const endTime = toIdx + 1 < TIME_SLOTS.length ? TIME_SLOTS[toIdx + 1].label : '21:15';
    setPendingBlock({ date: dragDay, start_time: startTime, end_time: endTime });
    setReason('');
    setDragStart(null);
    setDragEnd(null);
    setDragDay(null);
  };

  const isInDragRange = (dayStr, slotLabel) => {
    if (!isDragging || dayStr !== dragDay) return false;
    const sIdx = slotIndex(dragStart);
    const eIdx = slotIndex(dragEnd);
    const cIdx = slotIndex(slotLabel);
    return cIdx >= Math.min(sIdx, eIdx) && cIdx <= Math.max(sIdx, eIdx);
  };

  const getSlotForCell = (dayStr, slotLabel) => {
    return slots.filter(s => s.date === dayStr && s.start_time <= slotLabel && s.end_time > slotLabel);
  };

  const getBlockForCell = (dayStr, slotLabel) => {
    return blocks.find(b => b.date === dayStr && b.start_time <= slotLabel && b.end_time > slotLabel);
  };

  const submitBlock = async () => {
    if (!pendingBlock) return;
    if (!reason.trim()) {
      toast.error(lang === 'es' ? 'Introduce un motivo' : 'Enter a reason');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/schedule/blocks`, {
        study_id: studyId,
        date: pendingBlock.date,
        start_time: pendingBlock.start_time,
        end_time: pendingBlock.end_time,
        reason: reason.trim(),
      }, { withCredentials: true });
      await loadData();
      setPendingBlock(null);
      if (data.conflicts && data.conflicts.length > 0) {
        setConflicts(data.conflicts);
      } else {
        toast.success(lb.saved);
      }
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/schedule/blocks/${toDelete.block_id}`, { withCredentials: true });
      toast.success(lb.deleted);
      setToDelete(null);
      await loadData();
    } catch (err) {
      toast.error(errText(err));
    }
    setDeleting(false);
  };

  if (!studyId) {
    return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero' : 'Save the study first'}</p>;
  }

  const blocksSorted = [...blocks].sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`));

  return (
    <div className="space-y-4" data-testid="schedule-blocks-panel">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <Prohibit size={18} className="text-red-500" />
          {lb.title}
        </h3>
        <p className="text-xs text-stone-500 mt-0.5 max-w-3xl">{lb.subtitle}</p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="h-8 w-8 rounded-sm" data-testid="blocks-prev-week">
          <CaretLeft size={16} />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="rounded-sm text-xs" data-testid="blocks-today">
          {lb.today}
        </Button>
        <Button variant="outline" size="icon" onClick={() => navigate(1)} className="h-8 w-8 rounded-sm" data-testid="blocks-next-week">
          <CaretRight size={16} />
        </Button>
        <span className="text-sm font-semibold text-stone-700 ml-2">
          {days[0].getDate()} - {days[6].getDate()} {MONTHS[days[6].getMonth()]} {days[6].getFullYear()}
        </span>

        {/* Legend */}
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: hexToRgba(studyColor, 0.18), border: `1px solid ${hexToRgba(studyColor, 0.5)}` }} />
            <span className="text-xs text-stone-500">{lb.schedule_legend}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{
              backgroundColor: '#dc2626',
              opacity: 0.5,
              backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 2px, rgba(255,255,255,0.55) 2px 4px)',
            }} />
            <span className="text-xs text-red-600 font-medium">{lb.block_legend}</span>
          </div>
        </div>
      </div>

      <p className="text-xs text-stone-400 italic">{lb.drag_hint}</p>

      {/* Calendar grid */}
      <div
        className="bg-white border border-stone-200 rounded-sm overflow-hidden select-none"
        ref={tableRef}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { if (isDragging) handleMouseUp(); }}
      >
        {/* Header */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-stone-200 bg-stone-50">
          <div className="p-2" />
          {days.map((d, i) => {
            const ds = fmtDate(d);
            return (
              <div key={i} className={`p-2 text-center border-l border-stone-200 ${ds === today ? 'bg-[#1f3e80]/5' : ''}`}>
                <p className="text-xs text-stone-500">{WEEKDAYS[i]}</p>
                <p className={`text-sm font-semibold ${ds === today ? 'text-[#1f3e80]' : 'text-stone-800'}`}>{d.getDate()}</p>
              </div>
            );
          })}
        </div>
        {/* Time grid */}
        <div className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
          {TIME_SLOTS.map(slot => {
            const isHour = slot.m === 0;
            return (
              <div key={slot.label} className={`grid grid-cols-[56px_repeat(7,1fr)] ${isHour ? 'min-h-[28px]' : 'min-h-[24px]'}`}>
                <div className={`px-1 border-r border-stone-100 text-right ${isHour ? 'py-1 text-xs font-mono text-stone-400' : 'py-0.5 text-[10px] font-mono text-stone-300'}`}>
                  {slot.label}
                </div>
                {days.map((d, i) => {
                  const ds = fmtDate(d);
                  const inDrag = isInDragRange(ds, slot.label);
                  const mySlots = getSlotForCell(ds, slot.label);
                  const block = getBlockForCell(ds, slot.label);
                  const isFirstOfBlock = block && block.start_time === slot.label;

                  return (
                    <div
                      key={i}
                      className="border-l border-stone-100 relative cursor-crosshair transition-colors"
                      style={inDrag ? { backgroundColor: 'rgba(220,38,38,0.30)' } : {}}
                      onMouseDown={() => handleMouseDown(ds, slot.label)}
                      onMouseMove={() => handleMouseMove(ds, slot.label)}
                      data-testid={`blocks-cell-${ds}-${slot.label}`}
                    >
                      {/* Faint study scheduling */}
                      {!inDrag && mySlots.length > 0 && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            backgroundColor: hexToRgba(studyColor, 0.18),
                            borderLeft: `2px solid ${hexToRgba(studyColor, 0.5)}`,
                          }}
                        />
                      )}
                      {/* Existing block */}
                      {block && (
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundColor: '#dc2626',
                            opacity: 0.25,
                            backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(255,255,255,0.45) 4px 8px)',
                            border: '1px solid rgba(220,38,38,0.5)',
                            zIndex: 5,
                          }}
                          title={`Bloqueo: ${block.reason || ''}`}
                        />
                      )}
                      {isFirstOfBlock && (
                        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-1 z-20">
                          <span
                            className="text-[9px] text-red-700 font-semibold truncate"
                            title={block.reason}
                          >
                            ⛔ {block.reason}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setToDelete(block); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="text-red-500 hover:text-red-700 p-0 leading-none"
                            data-testid={`delete-block-${block.block_id}`}
                            title={lb.del_title}
                          >
                            <Trash size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Active blocks summary */}
      <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
        <p className="text-xs font-semibold text-stone-600 mb-2">
          {lb.list_title} — {blocks.length}
        </p>
        {blocksSorted.length === 0 ? (
          <p className="text-xs text-stone-400 italic">{lb.empty}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {blocksSorted.map(b => (
              <Badge
                key={b.block_id}
                variant="outline"
                className="text-xs gap-1 cursor-pointer hover:bg-red-50 border-red-200 text-red-700"
                onClick={() => setToDelete(b)}
                title={b.reason}
                data-testid={`block-badge-${b.block_id}`}
              >
                {b.date} {b.start_time}-{b.end_time} · {b.reason}
                <Trash size={10} className="text-red-400" />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Pending block dialog (reason prompt) */}
      <Dialog open={!!pendingBlock} onOpenChange={(v) => { if (!v && !saving) setPendingBlock(null); }}>
        <DialogContent className="max-w-md" data-testid="block-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Prohibit size={18} className="text-red-500" />
              {lb.new_block_title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">{lb.new_block_desc}</p>
          {pendingBlock && (
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-sm">
              <p className="text-xs text-stone-500">{lb.range}</p>
              <p className="font-medium text-stone-800 font-mono">
                {pendingBlock.date} · {pendingBlock.start_time} → {pendingBlock.end_time}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{lb.reason} <span className="text-red-500">*</span></Label>
            <Textarea
              data-testid="block-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[70px]"
              placeholder={lb.reason_ph}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingBlock(null)} disabled={saving}>{lb.cancel}</Button>
            <Button
              data-testid="save-block"
              onClick={submitBlock}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? '...' : lb.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflicts warning */}
      <Dialog open={!!conflicts} onOpenChange={(v) => { if (!v) setConflicts(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <Warning size={20} />{lb.conflicts_title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">{lb.conflicts_subtitle}</p>
          <div className="flex-1 overflow-y-auto border border-amber-200 bg-amber-50 rounded-sm">
            <Table className="dense-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{lb.start}</TableHead>
                  <TableHead>{lang === 'es' ? 'Voluntario' : 'Volunteer'}</TableHead>
                  <TableHead>{lang === 'es' ? 'Visita' : 'Visit'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(conflicts || []).map((c, i) => (
                  <TableRow key={c.appointment_id || i}>
                    <TableCell className="font-mono text-xs">{c.time}</TableCell>
                    <TableCell className="text-sm">{c.volunteer_name}</TableCell>
                    <TableCell className="text-xs text-stone-500">{c.visit_code}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button onClick={() => setConflicts(null)}>{lb.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(v) => { if (!v) setToDelete(null); }}
        title={lb.del_title}
        description={lb.del_desc}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
